import React from 'react';
import {
  FDBox,
  FDPrimaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import {
  useTabContentContext,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Context';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import TaxStrategyReviewTabLayout
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/TaxStrategy/TaxStrategyTabs/TaxStrategyReviewTabLayout';
import TaxStrategySummaryTabLayout
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/TaxStrategy/TaxStrategyTabs/TaxStrategySummaryTabLayout';
import TaxStrategySettingsTabLayout
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/TaxStrategy/TaxStrategyTabs/TaxStrategySettingsTabLayout';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import TaxStrategyTabList
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/TabTemplatePicker/TaxStrategy/TaxStrategyTabs/TaxStrategyTabList';
import fr from 'facet-rest';
import { dataAttrs } from 'Helpers/Testing';

const LAYOUTS = {
  FacetPlanTaxStrategySettingsTab: TaxStrategySettingsTabLayout,
  FacetPlanTaxStrategyReviewTab: TaxStrategyReviewTabLayout,
  FacetPlanTaxStrategySummaryTab: TaxStrategySummaryTabLayout,
};

const getLayout = (type) => {
  const layout = LAYOUTS[type];
  if (!layout) {
    throw new Error(`Did not find a layout for resource type ${type}!`);
  }
  return layout;
};

const TaxStrategyTabTemplate = () => {
  const { activeTabResource, setActiveTabResource } = useTabContentContext();
  const [loading, setLoading] = React.useState(false);
  const [isTabSwitching, setIsTabSwitching] = React.useState(false);
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const [currentTaxStrategyTab, setCurrentTaxStrategyTab] = React.useState(activeTabResource.attr('settingsTab').resources().head());

  const updateTaxStrategyValues = async (applyToChart) => {
    setLoading(true);

    try {
      if (!applyToChart || !activeTabResource.relationship(fr.relationships.saveAndGenerateTaxDetails.name)) {
        const [updatedActiveTabResource] = await activeTabResource.submit();
        setActiveTabResource(updatedActiveTabResource);
      }
      if (applyToChart) {
        await activeTabResource.submit();
        const [updatedActiveTabResource] = await activeTabResource.relationship(fr.relationships.saveAndGenerateTaxDetails.name)?.link('related').put(null, { deprecatedAllowAlterOfRelation: true });
        setActiveTabResource(updatedActiveTabResource);
        // Don't wait for the entire page refresh in case they want to make additional changes and we already have a page loading indicator
        setLoading(false);
        await refreshPageContentCollection();
      }
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to update tax strategy');
    } finally {
      setLoading(false);
    }
  };

  const onTabChange = async (newTaxStrategyTab, applyToChart) => {
    setIsTabSwitching(true);
    try {
      setCurrentTaxStrategyTab(newTaxStrategyTab);
      await updateTaxStrategyValues(applyToChart);
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to change tabs');
    } finally {
      setIsTabSwitching(false);
    }
  };

  const LayoutComponent = getLayout(currentTaxStrategyTab?.type());

  return (
    <FDStack direction={'row'} spacing={2} paddingX={3} paddingY={1}>
      <FDStack direction={'row'} spacing={8} paddingX={3} paddingY={1}>
        <FDStack direction={'column'} spacing={4} minWidth={'175px'}>
          <TaxStrategyTabList resource={activeTabResource} currentTaxStrategyTab={currentTaxStrategyTab} onTabChange={onTabChange} />
          <FDStack direction={'column'} spacing={2}>
            <FDPrimaryActionButton
              {...dataAttrs('tax-strategy-save-and-generate-btn')}
              label={'Save & Generate'}
              onClick={() => updateTaxStrategyValues(true)}
              loading={loading}
              isFullWidth={false}
            />
          </FDStack>
        </FDStack>
        <FDBox flexGrow={1}>
          <FDBox paddingY={2}>
            {isTabSwitching
              ? <FDBox paddingY={6}> <LoadingIndicator /> </FDBox>
              : <LayoutComponent
                resource={currentTaxStrategyTab}
                onSubmit={() => updateTaxStrategyValues(false)}
              /> }
          </FDBox>
        </FDBox>
      </FDStack>
    </FDStack>
  );
};

TaxStrategyTabTemplate.defaultProps = {
};

TaxStrategyTabTemplate.propTypes = {
};

export default TaxStrategyTabTemplate;
